import { useMemo, useRef, useState } from 'react';

import { Button, List, ListItem, Popover, Tooltip, Typography } from '@mui/material';
import { Dropdown } from '@truvhq/ui';
import { clsx } from 'clsx';
import PerfectScrollbar from 'react-perfect-scrollbar';

import type { TenantCompanyResponse } from 'api/types';

import { TenantCompaniesItem } from './TenantCompaniesItem';

type Props = {
  tenantCompanies: TenantCompanyResponse[];
  onSelectCompany: (id: string) => void;
  selectedCompanyId: string;
};

const plural = (amount: number) => (amount.toString().endsWith('1') ? `${amount} member` : `${amount} members`);

const mapTenantCompanyToCompany = ({ id, name, logo, members_count, owner_email }: TenantCompanyResponse) => ({
  id,
  name,
  logo_url: logo,
  value: `${plural(members_count)}\u00A0·\u00A0Owner: ${owner_email}`,
});

export const TenantCompanies = ({ tenantCompanies, onSelectCompany, selectedCompanyId }: Props) => {
  const [open, setOpen] = useState(false);
  const anchorEl = useRef(null);
  const companies = useMemo(() => tenantCompanies.map(mapTenantCompanyToCompany), [tenantCompanies]);
  const currentCompany = useMemo(
    () => tenantCompanies.find((company) => company.id === selectedCompanyId) ?? tenantCompanies[0],
    [tenantCompanies, selectedCompanyId],
  );
  const [isLoading, setIsLoading] = useState(false);

  return (
    <div ref={anchorEl} className="flex flex-col border-b-[1px] border-white/10 py-1.5" data-testid="tenantCompanies">
      <Tooltip
        arrow
        placement="right"
        title={`You are currently logged into ${currentCompany.name} (Owner: ${currentCompany.owner_email}). You can switch companies here.`}
      >
        <div className="flex w-full items-center gap-2">
          <Button
            disableRipple
            className={clsx(
              'relative -left-2 ml-6 mr-3 h-8 w-full flex-1 rounded-lg px-2 hover:bg-[#444444] active:bg-grey70',
            )}
            onClick={() => setOpen(true)}
          >
            <div className={clsx('mr-auto flex w-full items-center justify-between gap-2 text-white')}>
              <Typography noWrap className="font-medium" component="div" variant="body2">
                {currentCompany.name}
              </Typography>
              <Dropdown
                className={clsx(
                  'h-5 w-5 shrink-0 text-grey60 transition-all group-hover:text-grey80',
                  open && 'rotate-180',
                )}
              />
            </div>
          </Button>
        </div>
      </Tooltip>
      <Popover
        anchorEl={anchorEl.current}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        open={open}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        onClose={() => setOpen(false)}
      >
        <div className="flex w-[456px] flex-col px-2 py-4">
          <Typography className="mb-1 ml-2 font-medium">Switch companies</Typography>
          <div className="max-h-[180px] overflow-auto">
            <PerfectScrollbar options={{ suppressScrollX: true, suppressScrollY: companies.length < 4 }}>
              <List disablePadding className="w-full">
                {companies.map((company) => {
                  return (
                    <ListItem
                      key={company.id}
                      disablePadding
                      className="cursor-pointer justify-start rounded-[7px] p-2 font-medium hover:bg-grey20"
                      data-testid={`tenantCompany-${company.id}`}
                      onClick={() => {
                        setIsLoading(true);
                        onSelectCompany(company.id);
                      }}
                    >
                      <TenantCompaniesItem
                        company={company}
                        isLoading={isLoading}
                        isSelected={company.id === selectedCompanyId}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </PerfectScrollbar>
          </div>
        </div>
      </Popover>
    </div>
  );
};
